import { MsalProvider, useMsal, useMsalAuthentication } from '@azure/msal-react';
import { InteractionType, IPublicClientApplication } from "@azure/msal-browser";
import { loginRequest } from './authConfig';
import { isInIframe } from './utils/authUtils';
import { FramedAuthProvider, useFramedAuth } from './auth/FramedAuthContext';
import { Bounce, ToastContainer } from 'react-toastify';
import {  Routes, Route, HashRouter } from "react-router-dom";
import 'react-toastify/dist/ReactToastify.css';
import Home from './pages/Home';
import FAQ from './pages/FAQ';
import './App.scss';
import Layout from './Layout';
import Skills from './pages/Skills';
import SkillPathPage from './pages/SkillPath';
import AdminMenu from './pages/Admin';
import EditSkillPath from './pages/admin/EditSkillPath';
import EditQuiz from './pages/admin/EditQuiz';
import ComponentTestPage from './pages/UI/ComponentTestPage';
import Core from './pages/UI/Core';
import EditFAQs from './pages/admin/EditFAQs';
import EmployeeProgression from './pages/EmployeeProgression';
import SupervisorEmployeeProgression from './pages/SupervisorEmpProgression';
import Reports from './pages/admin/Reports';
import EditCompetencyMgmtPeriod from './pages/admin/EditCompetencyMgmtPeriod';
import MyCompetencies from './pages/MyCompetencies';
import SupervisorEmpCompetencies from './pages/SupervisorEmpCompetencies';
import { ROUTES } from './route-constants';

type AppProps = {
  instance: IPublicClientApplication;
};
/**
 * Most applications will need to conditionally render certain components based on whether a user is signed in or not. 
 * msal-react provides 2 easy ways to do this. AuthenticatedTemplate and UnauthenticatedTemplate components will 
 * only render their children if a user is authenticated or unauthenticated, respectively. For more, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */
const MainContent = () => {
  // Redirecting inside an iframe would navigate the frame away from the parent's UI, so
  // only auto-redirect to login at the top level when running standalone; framed sessions
  // rely on FramedAuthProvider (or the manual sign-in button's loginPopup) instead.
  useMsalAuthentication(isInIframe() ? InteractionType.Silent : InteractionType.Redirect);
  /**
   * useMsal is hook that returns the PublicClientApplication instance,
   * that tells you what msal is currently doing. For more, visit:
   * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/hooks.md
   */
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();
  const framedAuth = useFramedAuth();
  const isAuthenticated = !!activeAccount || !!framedAuth?.accessToken;

  const handleSignIn = () => {
    const request = {
      ...loginRequest,
      prompt: 'create',
    };
    const action = isInIframe() ? instance.loginPopup(request) : instance.loginRedirect(request);
    action.catch((error) => console.log(error));
  };
  return (
    <>
      {isAuthenticated ? (
        <HashRouter>
          <Routes>
            <Route path={ROUTES.home} element={<Layout />}>
              <Route index element={<Home />} />
              <Route path={ROUTES.faq} element={<FAQ />} />
              <Route path={ROUTES.skills.root} element={<Skills />} />
              <Route path={ROUTES.skills.byCategory} element={<Skills />} />
              <Route path={ROUTES.skillPath.byId} element={<SkillPathPage />} />
              <Route path={ROUTES.skillPath.byParentAndId} element={<SkillPathPage />} />
              <Route path={ROUTES.admin.root} element={<AdminMenu />} />
              <Route path={ROUTES.admin.editSkillPath.byId} element={<EditSkillPath />} />
              <Route path={ROUTES.admin.editSkillPath.byParentAndId} element={<EditSkillPath />} />
              <Route path={ROUTES.admin.editFaqs} element={<EditFAQs />} />
              <Route path={ROUTES.admin.editQuiz} element={<EditQuiz />} />
              <Route path={ROUTES.admin.editCompetencyPeriod} element={<EditCompetencyMgmtPeriod />} />
              <Route path={ROUTES.supervisor.employeeList} element={<SupervisorEmployeeProgression />} />
              <Route path={ROUTES.competencies.root} element={<MyCompetencies />} />
              <Route path={ROUTES.competencies.byUpn} element={<SupervisorEmpCompetencies />} />
              <Route path={ROUTES.bookmarks.root} element={<EmployeeProgression />} />
              <Route path={ROUTES.bookmarks.byUpn} element={<EmployeeProgression />} />
              <Route path={ROUTES.reports} element={<Reports />} />
              <Route path={ROUTES.uiTestPage} element={<ComponentTestPage />} />
              <Route path={ROUTES.uiCore} element={<Core />} />
            </Route>
          </Routes>
        </HashRouter>
      ) : (
        <input type="button" className="signInButton" onClick={handleSignIn} value="Log in" />
      )}
      <ToastContainer position="top-center"
          autoClose={5000} 
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick={true}
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          transition={Bounce} />
    </>
  );
};

const App = ({ instance }: AppProps) => {
  return (
    <div className="app main-scrolling">
      <MsalProvider instance={instance}>
        <FramedAuthProvider>
          <MainContent />
        </FramedAuthProvider>
      </MsalProvider>
    </div>);
}
export default App;
